import { useState } from "react";
import { useRecordContext, useTranslate, WithRecord } from "ra-core";
import { ArrayField } from "@/components/admin/array-field";
import { SingleFieldList } from "@/components/admin/single-field-list";
import { TextField } from "@/components/admin/text-field";
import { EmailField } from "@/components/admin/email-field";
import { Mail, Phone, Check } from "lucide-react";
import type { ReactNode } from "react";
import {
  contactGender,
  translateContactGenderLabel,
  translatePersonalInfoTypeLabel,
} from "./contactModel";
import type { Contact } from "../types";
import {
  WhatsAppAction,
  type WhatsAppActionContact,
} from "../whatsapp/WhatsAppAction";

export const ContactPersonalInfo = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();

  if (!record) return null;

  return (
    <div>
      <ArrayField source="email_jsonb">
        <SingleFieldList className="flex-col gap-y-0">
          <EmailRow />
        </SingleFieldList>
      </ArrayField>

      {record.has_newsletter && (
        <p className="pl-6 py-1 text-sm text-muted-foreground">
          {translate("resources.contacts.fields.has_newsletter")}
        </p>
      )}

      <ArrayField source="phone_jsonb">
        <SingleFieldList className="flex-col gap-y-0">
          <PhoneRow contact={record} />
        </SingleFieldList>
      </ArrayField>
      {contactGender
        .map((genderOption) => {
          if (record.gender === genderOption.value) {
            return (
              <PersonalInfoRow
                key={genderOption.value}
                icon={
                  <genderOption.icon className="w-4 h-4 text-muted-foreground" />
                }
                primary={
                  <div>
                    {translateContactGenderLabel(genderOption, translate)}
                  </div>
                }
              />
            );
          }
          return null;
        })
        .filter(Boolean)}
    </div>
  );
};

const EmailRow = () => {
  const record = useRecordContext<{ email: string }>();
  const translate = useTranslate();
  const [copied, setCopied] = useState(false);

  if (!record) return null;

  const handleCopy = () => {
    navigator.clipboard.writeText(record.email).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <PersonalInfoRow
      icon={
        <button
          type="button"
          onClick={handleCopy}
          title={translate("crm.common.copy")}
          aria-label={translate("crm.common.copy")}
          // Invisible hit-area growth to the 44px mobile touch-target floor
          // (design-system.md section 8), same pattern as TagChip's unlink
          // control: negative margin + matching padding leave the visible
          // icon size unchanged.
          className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer -m-3.5 p-3.5 md:m-0 md:p-0 inline-flex items-center justify-center"
        >
          {copied ? (
            <Check className="w-4 h-4 text-green-500" />
          ) : (
            <Mail className="w-4 h-4" />
          )}
        </button>
      }
      primary={<EmailField source="email" />}
    />
  );
};

/**
 * Almikia Epic EW1 (TASK-127): one phone row -- the number/type (unchanged
 * from before) plus the TASK-124 shared WhatsApp action for THAT number.
 * `SingleFieldList` provides only the row's own record (`{ number, type }`)
 * via context, so the parent contact (id, first_name) is passed as a prop
 * instead of read from context here.
 */
const PhoneRow = ({ contact }: { contact: Contact }) => {
  const translate = useTranslate();
  const phoneRecord = useRecordContext<{ number: string; type: string }>();

  if (!phoneRecord) return null;

  const whatsAppContact: WhatsAppActionContact = {
    id: contact.id,
    first_name: contact.first_name,
    phone_jsonb: [{ number: phoneRecord.number }],
  };

  return (
    <div className="flex flex-row items-center gap-x-2 py-1 min-h-6">
      <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="flex flex-wrap gap-x-2 gap-y-0 text-sm flex-1 min-w-0">
        <TextField source="number" />
        {phoneRecord.type !== "Other" && (
          <span className="text-muted-foreground">
            {translatePersonalInfoTypeLabel(phoneRecord.type, translate)}
          </span>
        )}
      </div>
      <WhatsAppAction contact={whatsAppContact} className="shrink-0" />
    </div>
  );
};

const PersonalInfoRow = ({
  icon,
  primary,
  showType,
}: {
  icon: ReactNode;
  primary: ReactNode;
  showType?: boolean;
}) => {
  const translate = useTranslate();

  return (
    <div className="flex flex-row items-center gap-x-2 py-1 min-h-6">
      {icon}
      <div className="flex flex-wrap gap-x-2 gap-y-0 text-sm">
        {primary}
        {showType ? (
          <WithRecord
            render={(row) =>
              row.type !== "Other" && (
                <span className="text-muted-foreground">
                  {translatePersonalInfoTypeLabel(row.type, translate)}
                </span>
              )
            }
          />
        ) : null}
      </div>
    </div>
  );
};
